/***
 * Summary: here resides the scheme and roles for the fields of the network performing
 * 
 */
import { yupToFormErrors } from "formik";
import * as Yup from "yup";

const TODIS_NURBANNODES_SCHEMA = Yup.object().shape({
    //general section Rules:
    dateValidTo: Yup.date("Valid from is a Date").required().nullable(true),
    dateValidFrom: Yup.date("Valid to is a Date").required().nullable(true),
    dateValidFor: Yup.string("Valid for is a Year"),
    tenTCategory: Yup.string("tent category is alphanumeric").nullable(true),
    nodeName: Yup.string("Road ID is alphanumeric").nullable(true),
    nameResp: Yup.string("Road ID is alphanumeric").nullable(true),
    addressResp: Yup.string("Road ID is alphanumeric").nullable(true),
    contactPer: Yup.string("Road ID is alphanumeric").nullable(true),
    contactPos: Yup.string("Road ID is alphanumeric").nullable(true),
    contactNum: Yup.string()
        .transform((originalValue) => {
            // Si el valor comienza con '+', eliminamos ese símbolo
            return typeof originalValue == 'string' ? originalValue.replace(/^\+/, '') : originalValue;
        })
        .nullable(true)
        .test('is-valid', 'Invalid data. \'Phone Number\' must be a valid number', function (value) {
            // Permitir números, espacios, y la presencia de '+'
            const isValid = /^[0-9+\s]+$/.test(value) || value == null || value == undefined;

            if (!isValid) {
                throw this.createError({ message: 'Invalid data. \'Phone Number\' must be a valid number' });
            }

            return true;
        }),

    contactEmail: Yup.string("Email")
        .transform((originalValue) => {
            // Realizar transformaciones si es necesario
            return typeof originalValue == 'string' ? originalValue.trim() : originalValue;
        })
        .nullable(true)
        .test('is-email', 'Invalid data. \'Email\' must be valid', function (value) {
            // Si el valor es null, lo consideramos válido y no aplicamos más validaciones
            if (value == null) {
                return true;
            }

            const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
            const emails = value.split(/[,;:]+/);  // Separar por comas, punto y coma, o dos puntos

            for (const email of emails) {
                if (!emailRegex.test(email.trim())) {
                    throw this.createError({ message: `Invalid data. '${email}' must be a valid email address` });
                }
            }

            return true;
        }),

    regionalPartner: Yup.string("Regional partner").nullable(true),
    ownershipType: Yup.string().nullable(true),
    owner1: Yup.string().nullable(true),
    ownershipType: Yup.string().nullable(true),
    owner1: Yup.string().nullable(true),
    owner1Per: Yup.number()
        .required("Majority Ownership Porcentage is required")
        .min(0, "Main ownership Porcentage must be greater than or equal to 0")
        .max(100, "Main ownership Porcentage must be less than or equal to 100")
        .test('total-percentage', 'Sum of ownership percentages should be 100%', function (value) {
            const owner1Per = parseFloat(this.parent.owner1Per) || 0;
            const ownerOtherPer = parseFloat(this.parent.ownerOtherPer) || 0;
            return owner1Per + ownerOtherPer === 100;
        }),
    ownerOther: Yup.string()
        .test('ownership-format', 'Invalid ownership format', function (value) {
            // Verificar el formato owner;porcentaje;owner;porcentaje
            if (!value) return true; // Permitir valor vacío
            const pairs = value.split(';');
            // Debe haber un número par de elementos
            if (pairs.length % 2 !== 0) return false;
            // Comprobar si cada par tiene un owner y un porcentaje válido
            for (let i = 0; i < pairs.length; i += 2) {
                const owner = pairs[i].trim();
                const percentage = parseFloat(pairs[i + 1].trim());
                if (!owner || isNaN(percentage) || percentage < 0 || percentage > 100) {
                    return false;
                }
            }
            return true;
        })
        .nullable(true),
    ownerOtherPer: Yup.number()
        .when('ownerOther', {
            is: value => value && value.trim().length > 0,
            then: Yup.number()
                .required('The percentage of secondary owners must be filled in if the "Secondary Owners" field is filled in.')
                .test('sum-of-percentages', 'Sum of secondary owners should match', function (value) {
                    const pairs = this.parent.ownerOther ? this.parent.ownerOther.split(';') : [];
                    let totalPercentage = 0;
                    for (let i = 1; i < pairs.length; i += 2) {
                        totalPercentage += parseFloat(pairs[i].trim());
                    }
                    return totalPercentage === value;
                })
        })
        .nullable(true),

    remarks: Yup.string("remarks").nullable(true),

    urbanNpmInfraDTO: Yup.object().shape({
        altFuelsElectricityHeavyIndividualOutput: Yup.number('Value should be number').nullable(true),
        altFuelsElectricityHeavyTotalOutput: Yup.number('Value should be number').nullable(true),
        altFuelsHydrogenRefuelling: Yup.boolean().nullable(true),
        sustainableMobilityPlan: Yup.boolean().nullable(true),
        mobilityIndicatorsDataCollection: Yup.boolean().nullable(true),
        multimodalPassengerStations: Yup.boolean().nullable(true),
        multimodalFreightTerminal: Yup.boolean().nullable(true),
        sumpContactPointExistence: Yup.boolean().nullable(true),
        sumpProgramExistence: Yup.boolean().nullable(true),
    }),
    urbanNpmTentcompDTO: Yup.object().shape({
        altFuelsElectricityHeavy: Yup.boolean().nullable(true),
        altFuelsHydrogenRefuelling: Yup.boolean().nullable(true),
        sustainableMobilityPlan: Yup.boolean().nullable(true),
        mobilityIndicatorsDataCollection: Yup.boolean().nullable(true),
        multimodalPassengerStations: Yup.boolean().nullable(true),
        multimodalFreightTerminal: Yup.boolean().nullable(true),
        sumpContactPointExistence: Yup.boolean().nullable(true),
        sumpProgramExistence: Yup.boolean().nullable(true),
    })

});

const NURBANNODES_INIT = {
    //general section Rules:
    dateValidTo: "",
    dateValidFrom: "",
    dateValidFor: "",
    tenTCategory: "",
    nodeName: "",
    nameResp: "",
    addressResp: "",
    contactPer: "",
    contactPos: "",
    contactNum: 0,
    contactEmail: "",
    regionalPartner: "",
    ownershipType: "",
    owner1: "",
    owner1Per: "",
    ownerOther: "",
    ownerOtherPer: "",
    remarks: "",
    urbanNpmInfraDTO: {
        altFuelsElectricityHeavyIndividualOutput: "",
        altFuelsElectricityHeavyTotalOutput: "",
        altFuelsHydrogenRefuelling: false,
        sustainableMobilityPlan: false,
        mobilityIndicatorsDataCollection: false,
        multimodalPassengerStations: false,
        multimodalFreightTerminal: false,
        sumpContactPointExistence: false,
        sumpProgramExistence: false,
    },
    urbanNpmTentcompDTO: {
        altFuelsElectricityHeavy: false,
        altFuelsHydrogenRefuelling: false,
        sustainableMobilityPlan: false,
        mobilityIndicatorsDataCollection: false,
        multimodalPassengerStations: false,
        multimodalFreightTerminal: false,
        sumpContactPointExistence: false,
        sumpProgramExistence: false,
    }
}

export { TODIS_NURBANNODES_SCHEMA, NURBANNODES_INIT };